import { CommonModule } from '@angular/common';
import { Component, OnDestroy, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { AuthSessionStorage } from '@smata-data-access';
import { ButtonDirective, SmataSingleInputComponent } from '@smata-shared';
import { AuthStore } from '../store/auth.store';

const RESEND_COOLDOWN_SECONDS = 90;

@Component({
  selector: 'smata-passcode',
  standalone: true,
  imports: [
    CommonModule,
    SmataSingleInputComponent,
    ButtonDirective,
    ReactiveFormsModule,
    MatInputModule,
  ],
  templateUrl: './passcode.component.html',
  styleUrl: './passcode.component.scss',
})
export class SmataPasscodeComponent implements OnDestroy {
  public readonly authStore = inject(AuthStore);
  readonly formControl = {
    passcode: new FormControl('', [Validators.required]),
  };

  private readonly form = new FormGroup(this.formControl);
  readonly contactType = AuthSessionStorage.getContactType();

  readonly resendCountdown = signal(0);
  private countdownIntervalId: ReturnType<typeof setInterval> | null = null;

  get resendCountdownLabel(): string {
    const totalSeconds = this.resendCountdown();
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  onLogin() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const passcode = this.form.getRawValue().passcode;
    if (!passcode) return;

    this.authStore.verifyPasscode(passcode);
  }

  onResend() {
    if (this.resendCountdown() > 0) return;

    this.authStore.onResendPasscode();
    this.startResendCountdown();
  }

  private startResendCountdown() {
    this.clearCountdown();
    this.resendCountdown.set(RESEND_COOLDOWN_SECONDS);
    this.countdownIntervalId = setInterval(() => {
      const remaining = this.resendCountdown() - 1;
      this.resendCountdown.set(remaining);
      if (remaining <= 0) {
        this.clearCountdown();
      }
    }, 1000);
  }

  private clearCountdown() {
    if (this.countdownIntervalId !== null) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
  }

  ngOnDestroy() {
    this.clearCountdown();
  }
}
